import { getContactUserId } from './ShareContactPicker'
import { P } from 'components'
import BreadCrumbs from 'components/breadcrumbs'
import PMBFileUploads from 'components/PMBFileUploads'
import PMBInputNumber from 'components/PMBInputNumber'
import PMBProjectSelections from 'components/PMBProjectSelections'
import PMBSelect from 'components/PMBSelect'
import ProfilePic from 'components/PMBProfilePic'
import { AnimatePresence, motion } from 'framer-motion'
import CreatableSelect from 'react-select/creatable'
import Flatpickr from 'react-flatpickr'
import { Indonesian } from 'flatpickr/dist/l10n/id'
import { capitalizeEveryWord } from 'helpers'
import useIsMobile from 'hooks/useIsMobile'
import InputAddressBook from '../address-book/InputAddressBook'
import ModalAddContact from '../address-book/ModalAddContact'
import {
  setIsModalOpenAddAddressBook,
  setSearchAddressBook,
} from '../address-book/addressBookSlice'
import { useSnackbar } from 'notistack'
import { useCallback, useEffect, useState, useRef } from 'react'
import { Trash2, Calendar, X } from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from 'reactstrap'
import { formatInputNumber } from 'utility/Utils'
import { URL_PROJECTS_MENU } from 'utils/pathUrl'
import { getComodities } from './projectsAction'
import {
  useCreateLinkTagsMutation,
  useCreateProjectMenuMutation,
  useFetchLinkTagsQuery,
  useFetchProjectByIdQuery,
  useListResourcesAccessQuery,
  useSharedProjectDetailQuery,
  useUpdateProjectMenuMutation,
  useUpdateProjectMenuSharedMutation,
} from './projectsMenuListApiSlice'

const FormAddEditProject = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const addressBookRef = useRef(null)
  const { isModalOpenAddAddressBook } = useSelector((e) => e.addressBook || {})

  const toggleAddContact = () => {
    dispatch(setIsModalOpenAddAddressBook(!isModalOpenAddAddressBook))
  }
  const location = useLocation()
  const { id: projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const fromDetail = location.state?.from === 'detail'
  const isMobile = useIsMobile()
  const [searchParams] = useSearchParams()
  const isShared = searchParams.get('shared') === 'true'

  const [createProjectMenu] = useCreateProjectMenuMutation()
  const [updateProjectMenu] = useUpdateProjectMenuMutation()
  const [updateProjectMenuShared] = useUpdateProjectMenuSharedMutation()
  const [createLinkTags] = useCreateLinkTagsMutation()

  const {
    data: ownData,
    isLoading: isLoadingProject,
    isFetching: isFetchingProject,
  } = useFetchProjectByIdQuery(
    { projectId: Number(projectId) },
    {
      skip: !projectId || isShared,
      refetchOnMountOrArgChange: true,
    },
  )

  const {
    data: sharedData,
    isLoading: isLoadingShared,
    refetch: refetchShared,
  } = useSharedProjectDetailQuery(
    { id: projectId },
    { skip: !projectId || !isShared },
  )

  const projectData = isShared ? sharedData : ownData

  const { data: resourceAccessData } = useListResourcesAccessQuery(
    { resourceId: projectId, resourceType: 'PROJECT' },
    { skip: !projectId },
  )

  const { data: linkTagsData, isLoading: isLoadingLinkTags } =
    useFetchLinkTagsQuery(
      {
        linkDocs: projectData?.linkDocs,
        page: 0,
        size: 50,
      },
      {
        skip: !projectData?.linkDocs || !projectId,
      },
    )

  const { comodities: listComodities } = useSelector((s) => s.projectsMenuList)

  const getTodayDate = () => {
    const today = new Date()
    return today.toISOString().split('T')[0]
  }

  const [payloadNew, setPayloadNew] = useState({
    assigneeUser: null,
    label: '',
    title: '',
    description: '',
    status: 'ACTIVE',
    startDate: !projectId ? getTodayDate() : '',
    endDate: '',
    participants: [],
    goals: [
      {
        itemId: '',
        unit: '',
        amount: '',
        currentAmount: '',
      },
    ],
    attachments: [],
    tags: [],
  })

  const [isClick, setIsClick] = useState(false)
  const [loadingStatus, setLoadingStatus] = useState(null)
  const [hasEndDate, setHasEndDate] = useState(false)
  const [selectedProject, setSelectedProject] = useState(null)

  useEffect(() => {
    if (!listComodities.length) {
      dispatch(getComodities())
    }
  }, [dispatch, listComodities.length])

  // LOAD RECENT PARTICIPANTS (CACHE)
  useEffect(() => {
    if (!projectId) {
      try {
        const cached = localStorage.getItem('PMB_recent_project_participants')
        if (cached) {
          const parsed = JSON.parse(cached)
          if (Array.isArray(parsed) && parsed.length > 0) {
            setPayloadNew((prev) => {
              if (prev.participants.length === 0) {
                return { ...prev, participants: parsed }
              }
              return prev
            })
          }
        }
      } catch (error) {
        console.error('Failed to load recent participants cache', error)
      }
    }
  }, [projectId])

  // EDIT PROJECT
  useEffect(() => {
    if (projectData && projectId) {
      const project = projectData
      const hasEndDateValue = !!project.endDate

      setHasEndDate(hasEndDateValue)

      setSelectedProject({
        value: project?.parentProject?.id,
        label: project?.parentProject?.label,
        title: project?.parentProject?.title,
      })
      setPayloadNew({
        assigneeUser: {
          value: project?.assigneeUserId || null,
          label: project?.assigneeUser || '',
        },
        label: project.label || '',
        title: project.title || '',
        description: project.description || '',
        status: project.status || 'ACTIVE',
        startDate: project.startDate
          ? new Date(project.startDate).toISOString().split('T')[0]
          : '',
        endDate: project.endDate
          ? new Date(project.endDate).toISOString().split('T')[0]
          : '',
        participants:
          (resourceAccessData?.participants || project.participants)
            ?.filter((p) => p.role !== 'OWNER')
            ?.map((p) => ({
              value: p.userId,
              label: p.name || p.userId,
              email: p.email || '',
              profileImg: p.profileImg || null,
            })) || [],
        goals:
          project.goals && project.goals.length > 0
            ? project.goals.map((goal) => ({
              id: goal.id,
              itemId: goal.itemId ? goal.itemId.toString() : '',
              nameItem: goal.nameItem || '',
              unit: goal.unit || '',
              amount: goal.amount?.toString() || '',
              currentAmount: goal.currentAmount?.toString() || '',
            }))
            : [
              {
                itemId: '',
                nameItem: '',
                unit: '',
                amount: '',
                currentAmount: '',
              },
            ],
        attachments: project.linkDocs
          ? project.linkDocs.split(',').map((url, idx) => {
            const trimmedUrl = url.trim()
            const linkTag = linkTagsData?.items?.find(
              (item) => item.linkName === trimmedUrl,
            )
            return {
              id: Date.now() + idx,
              fileUrl: trimmedUrl,
              fileName: trimmedUrl.split('/').pop(),
              tag: linkTag?.tagName || '',
            }
          })
          : [],
        tags: project.tags ? project.tags.map(t => typeof t === 'object' ? t.tag : t) : [],
      })
    }
  }, [projectData, projectId, linkTagsData, resourceAccessData])

  const handleSelectContact = (contact) => {
    const userId = getContactUserId(contact)

    if (!userId) return

    setPayloadNew((prev) => {
      if (String(prev.assigneeUser?.value) === String(userId)) return prev
      const exists = prev.participants.find(
        (p) => String(p.value) === String(userId),
      )
      if (exists) return prev

      return {
        ...prev,
        participants: [
          ...prev.participants,
          {
            value: userId,
            label: contact.relationName || contact.fullName || contact.name,
            email:
              contact.relationEmail ||
              contact.relationPhoneNumber ||
              contact.email ||
              contact.phoneNumber,
            profileImg: contact.profile_img || contact.profileImg,
          },
        ],
      }
    })
  }

  const handleSelectAssignee = (contact) => {
    const userId = getContactUserId(contact)

    if (!userId) {
      setPayloadNew((prev) => ({ ...prev, assigneeUser: null }))
      return
    }

    const name = contact.relationName || contact.fullName || contact.name
    const phoneNumber = contact.relationPhoneNumber || contact.phoneNumber
    setPayloadNew((prev) => ({
      ...prev,
      participants: prev.participants.filter(
        (participant) => String(participant.value) !== String(userId),
      ),
      assigneeUser: {
        value: userId,
        label: phoneNumber ? `${name} (${phoneNumber})` : name,
      },
    }))
  }

  const handleAddGoal = () => {
    setPayloadNew((prev) => ({
      ...prev,
      goals: [
        ...prev.goals,
        {
          itemId: '',
          unit: '',
          amount: '',
          currentAmount: '',
        },
      ],
    }))
  }

  const handleRemoveGoal = (index) => {
    setPayloadNew((prev) => ({
      ...prev,
      goals: prev.goals.filter((_, i) => i !== index),
    }))
  }

  const handleGoalChange = (index, field, value) => {
    setPayloadNew((prev) => ({
      ...prev,
      goals: prev.goals.map((goal, i) => {
        if (i === index) {
          if (field === 'itemId') {
            const selectedCommodity = listComodities?.find(
              (item) => item.value?.toString() === value?.toString(),
            )
            return {
              ...goal,
              [field]: value,
              unit: selectedCommodity?.unit || '',
            }
          }
          return { ...goal, [field]: value }
        }
        return goal
      }),
    }))
  }

  const handleAmountChange = useCallback((e, index, field = 'amount') => {
    const value = e.target.value
    const replaceValue = value.replace(/[^\d,]/g, '')

    const [integerPart, decimalPart] = replaceValue.split(',')

    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.')

    const formattedValue = decimalPart
      ? `${formattedInteger},${decimalPart}`
      : formattedInteger

    const numericValue =
      parseFloat(formattedValue.replace(/\./g, '').replace(',', '.')) || null

    setPayloadNew((prev) => ({
      ...prev,
      goals: prev.goals.map((goal, i) => {
        if (i === index) {
          return {
            ...goal,
            [field]: numericValue,
          }
        }

        return goal
      }),
    }))
  }, [])

  const isEdit = !!projectId
  const isClosed = payloadNew.status === 'CLOSED'
  const isFieldDisabled = isClosed || (isEdit && payloadNew.status === 'ACTIVE')

  // FORMAT COMMODITY OPTIONS WITH UNIT DISPLAY
  const formatCommodityOptions = (commodities) => {
    return commodities?.map((item) => ({
      ...item,
      label: item.label,
      unitDisplay: item.unit ? capitalizeEveryWord(item.unit) : '',
    }))
  }

  // CUSTOM FORMAT FOR OPTION LABEL IN DROPDOWN
  const formatOptionLabel = ({ label, unitDisplay }) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        gap: '8px',
        borderRadius: '100px',
      }}
    >
      <span>{label}</span>

      {unitDisplay && (
        <>
          <span
            style={{
              width: '4px',
              height: '4px',
              backgroundColor: 'gray',
              borderRadius: '50%',
              flexShrink: 0,
            }}
          />
          <span>{unitDisplay}</span>
        </>
      )}
    </div>
  )

  // GET AVAILABLE COMMODITIES FOR A SPECIFIC GOAL INDEX (FILTER OUT DUPLICATES WITH SAME UNIT)
  const getAvailableCommodities = (currentIndex) => {
    const formattedOptions = formatCommodityOptions(listComodities)

    // GET ALL SELECTED COMMODITY-UNIT COMBINATIONS EXCEPT CURRENT INDEX
    const selectedCombinations = payloadNew.goals
      .map((goal, idx) => {
        if (idx === currentIndex) return null
        return goal.itemId ? `${goal.itemId}-${goal.unit}` : null
      })
      .filter(Boolean)

    // FILTER OUT COMMODITIES THAT ARE ALREADY SELECTED WITH THE SAME UNIT
    return formattedOptions?.filter((commodity) => {
      const combination = `${commodity.value}-${commodity.unit}`
      return !selectedCombinations.includes(combination)
    })
  }

  const getAvailableStatusButtons = () => {
    if (!isEdit) {
      return [
        { status: 'DRAFT', label: 'Simpan Draft', color: 'primary' },
        { status: 'ACTIVE', label: 'Simpan & Aktifkan', color: 'primary' },
      ]
    }

    const currentStatus = projectData?.data?.status || payloadNew.status
    if (currentStatus === 'DRAFT') {
      return [
        { status: 'ACTIVE', label: 'Simpan & Aktifkan', color: 'primary' },
      ]
    } else if (currentStatus === 'ACTIVE') {
      return [{ status: 'CLOSED', label: 'Simpan Closed', color: 'danger' }]
    } else if (currentStatus === 'CLOSED') {
      return []
    }

    return []
  }

  const handleSubmit = async (status) => {
    setIsClick(true)

    if (!payloadNew?.assigneeUser) {
      enqueueSnackbar('Penugasan wajib diisi', {
        variant: 'error',
      })
      return
    }

    if (payloadNew.label === '') {
      enqueueSnackbar('Nomor wajib diisi', {
        variant: 'error',
      })
      return
    }

    if (payloadNew.title === '') {
      enqueueSnackbar('Judul wajib diisi', {
        variant: 'error',
      })
      return
    }

    if (isEdit && status === (projectData?.data?.status || payloadNew.status)) {
      setLoadingStatus('SAVE')
    } else {
      setLoadingStatus(status)
    }

    try {
      const linkDocsUrls = Array.isArray(payloadNew.attachments)
        ? payloadNew.attachments
          .map((file) => file.fileUrl)
          .filter(Boolean)
          .join(',')
        : ''

      const formattedPayload = {
        assigneeUserId: payloadNew?.assigneeUser?.value || undefined,
        parentProjectId: selectedProject?.value || undefined,
        label: payloadNew.label,
        title: payloadNew.title || undefined,
        description: payloadNew.description || undefined,
        status: status,
        startDate: payloadNew.startDate
          ? new Date(payloadNew.startDate).toISOString()
          : undefined,
        endDate: hasEndDate
          ? payloadNew.endDate
            ? new Date(payloadNew.endDate).toISOString()
            : undefined
          : null,
        shareUserIds: payloadNew.participants.map((p) => p.value),
        tags: payloadNew.tags && payloadNew.tags.length > 0 ? payloadNew.tags : undefined,
        goals: payloadNew.goals
          .filter((goal) => goal.itemId && goal.unit && goal.amount)
          .map((goal) => ({
            ...(goal.id && { id: goal.id }),
            itemId: goal.itemId,
            unit: goal.unit,
            amount: goal.amount || 0,
            currentAmount: goal.currentAmount || 0,
          })),
        linkDocs: linkDocsUrls || undefined,
      }

      let response
      if (isEdit) {
        if (isShared) {
          response = await updateProjectMenuShared({
            projectId,
            payload: formattedPayload,
          })
        } else {
          response = await updateProjectMenu({
            projectId,
            payload: formattedPayload,
          })
        }
      } else {
        response = await createProjectMenu(formattedPayload)
      }

      if (!response?.error) {
        if (payloadNew.participants && payloadNew.participants.length > 0) {
          localStorage.setItem(
            'PMB_recent_project_participants',
            JSON.stringify(payloadNew.participants)
          )
        } else {
          localStorage.removeItem('PMB_recent_project_participants')
        }

        if (payloadNew.attachments.length > 0) {
          const linkTagsPayload = {
            linkTags: payloadNew.attachments
              .filter((file) => file.fileUrl && file.tag)
              .map((file) => ({
                linkName: file.fileUrl,
                tagName: file.tag,
              })),
          }

          if (linkTagsPayload.linkTags.length > 0) {
            await createLinkTags(linkTagsPayload)
          }
        }

        enqueueSnackbar(
          `Project berhasil ${isEdit ? 'diubah' : 'ditambahkan'}`,
          {
            variant: 'success',
          },
        )
        navigate(-1)
      } else {
        enqueueSnackbar(
          response?.error?.data?.message ??
          `Project gagal ${isEdit ? 'diubah' : 'ditambahkan'}`,
          {
            variant: 'error',
          },
        )
      }
    } catch (error) {
      enqueueSnackbar(
        error?.data?.message ??
        `Project gagal ${isEdit ? 'diubah' : 'ditambahkan'}`,
        {
          variant: 'error',
        },
      )
    } finally {
      setLoadingStatus(null)
    }
  }

  if (isLoadingProject || isFetchingProject) {
    return (
      <Container className="custom-container d-flex justify-content-center align-items-center py-5">
        <Spinner color="primary" />
      </Container>
    )
  }

  return (
    <>
      <ModalAddContact
        isOpen={isModalOpenAddAddressBook}
        toggle={toggleAddContact}
        isOutsideAddressBook
      />
      <Container className="custom-container d-flex flex-column gap-3 py-3">
        <div className="mt-3">
          <BreadCrumbs
            breadCrumbParent="Daftar Projects"
            breadCrumbParentLink={
              isShared ? `${URL_PROJECTS_MENU}?shared=true` : URL_PROJECTS_MENU
            }
            {...(fromDetail && isEdit
              ? {
                breadCrumbParent2: projectData?.title || 'Detail Project',
                breadCrumbParent2Link: `/projects/${projectId}${isShared ? '?shared=true' : ''}`,
              }
              : {})}
            breadCrumbActive={isEdit ? 'Ubah Project' : 'Tambah Project'}
          />
        </div>

        <Card className="border-0 shadow-sm">
          <CardBody className="d-flex flex-column gap-2">
            <h5 className="mb-2">{isEdit ? 'Ubah Project' : 'Tambah Project'}</h5>

            <FormGroup>
              <Label className="text-sm pb-1">
                Penerima Tugas <span style={{ color: '#b42218' }}>*</span>
              </Label>
              <InputAddressBook
                title=""
                placeholderDesc="Pilih peserta"
                onSelectContact={handleSelectAssignee}
                fieldName="assigneeUser"
                valueName={payloadNew.assigneeUser?.label || ''}
                invalid={!payloadNew?.assigneeUser && isClick}
                disabled={isFieldDisabled}
                useCombinedContacts
              />
            </FormGroup>

            <FormGroup>
              <Label className="text-sm pb-1">Induk Project</Label>
              <PMBProjectSelections
                isDisabled={isFieldDisabled}
                className="border-input button w-100"
                placeholder="Pilih project"
                value={selectedProject}
                onChange={(project) => setSelectedProject(project)}
              />
            </FormGroup>

            <FormGroup>
              <Label className="text-sm pb-1">
                Nomor <span style={{ color: '#b42218' }}>*</span>
              </Label>
              <Input
                disabled={isFieldDisabled}
                placeholder="Masukkan nomor"
                autoComplete="off"
                name="label"
                id="label"
                onChange={(e) => {
                  setPayloadNew((prev) => ({
                    ...prev,
                    label: e.target.value,
                  }))
                }}
                value={payloadNew?.label ?? ''}
                invalid={!payloadNew?.label && isClick}
                data-testid="project-form-name-input"
              />
            </FormGroup>

            <FormGroup>
              <Label className="text-sm pb-1">
                Judul <span style={{ color: '#b42218' }}>*</span>
              </Label>
              <Input
                disabled={isFieldDisabled}
                placeholder="Masukkan judul"
                autoComplete="off"
                name="title"
                id="title"
                onChange={(e) => {
                  setPayloadNew((prev) => ({
                    ...prev,
                    title: e.target.value,
                  }))
                }}
                value={payloadNew?.title ?? ''}
                invalid={!payloadNew?.title && isClick}
              />
            </FormGroup>

            <FormGroup>
              <Label className="text-sm">Deskripsi</Label>
              <Input
                disabled={isFieldDisabled}
                type="textarea"
                placeholder="Masukkan deskripsi"
                autoComplete="off"
                name="description"
                id="description"
                rows="3"
                onChange={(e) => {
                  setPayloadNew((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }}
                value={payloadNew?.description ?? ''}
              />
            </FormGroup>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label className="text-sm">Tanggal Mulai</Label>
                  <div
                    className={`d-flex align-items-center justify-content-center border rounded ${isFieldDisabled ? 'bg-light' : ''}`}
                  >
                    <Calendar size={17} className="ms-3" />
                    <Flatpickr
                      disabled={isFieldDisabled}
                      options={{
                        locale: Indonesian,
                        dateFormat: 'd-M-Y',
                        defaultDate: payloadNew?.startDate
                          ? new Date(payloadNew.startDate)
                          : undefined,
                      }}
                      onChange={(selectedDate) => {
                        if (selectedDate && selectedDate[0]) {
                          const dateObj = selectedDate[0]
                          const newStartDate = new Date(
                            dateObj.getTime() -
                            dateObj.getTimezoneOffset() * 60000,
                          )
                            .toISOString()
                            .split('T')[0]

                          setPayloadNew((prev) => {
                            if (prev.endDate && newStartDate > prev.endDate) {
                              return {
                                ...prev,
                                startDate: newStartDate,
                                endDate: '',
                              }
                            }
                            return {
                              ...prev,
                              startDate: newStartDate,
                            }
                          })
                        }
                      }}
                      className={`form-control flat-picker bg-transparent border-0 shadow-none ${isFieldDisabled ? 'disabled' : ''}`}
                      placeholder="Pilih tanggal"
                    />
                  </div>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <Input
                      disabled={isFieldDisabled}
                      type="checkbox"
                      id="hasEndDateCheckbox"
                      checked={hasEndDate}
                      onChange={(e) => {
                        const checked = e.target.checked
                        setHasEndDate(checked)
                        if (!checked) {
                          setPayloadNew((prev) => ({
                            ...prev,
                            endDate: '',
                          }))
                        }
                      }}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <Label
                      htmlFor="hasEndDateCheckbox"
                      className="text-sm mb-0"
                      style={{ cursor: 'pointer' }}
                    >
                      Tanggal Selesai
                    </Label>
                  </div>
                  <div
                    className={`d-flex align-items-center justify-content-center border rounded ${!hasEndDate || isClosed ? 'bg-light' : ''}`}
                  >
                    <Calendar size={17} className="ms-3" />
                    <Flatpickr
                      disabled={!hasEndDate || isFieldDisabled}
                      options={{
                        locale: Indonesian,
                        dateFormat: 'd-M-Y',
                        minDate: payloadNew?.startDate
                          ? new Date(payloadNew.startDate)
                          : undefined,
                        defaultDate: payloadNew?.endDate
                          ? new Date(payloadNew.endDate)
                          : undefined,
                      }}
                      onChange={(selectedDate) => {
                        if (selectedDate && selectedDate[0]) {
                          const dateObj = selectedDate[0]
                          const newEndDate = new Date(
                            dateObj.getTime() -
                            dateObj.getTimezoneOffset() * 60000,
                          )
                            .toISOString()
                            .split('T')[0]

                          setPayloadNew((prev) => ({
                            ...prev,
                            endDate: newEndDate,
                          }))
                        } else {
                          setPayloadNew((prev) => ({
                            ...prev,
                            endDate: '',
                          }))
                        }
                      }}
                      className={`form-control flat-picker bg-transparent border-0 shadow-none ${!hasEndDate || isFieldDisabled ? 'disabled' : ''}`}
                      placeholder="Pilih tanggal"
                    />
                  </div>
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <P className="text-sm pb-1" size="regular" color="#344054">
                Peserta
              </P>
              <InputAddressBook
                title=""
                placeholderDesc="Cari nama atau nomor handphone"
                onSelectContact={handleSelectContact}
                fieldName="userId"
                onClickAdd={(localSearch) =>
                  dispatch(setSearchAddressBook(localSearch))
                }
                innerRef={addressBookRef}
                disabled={isClosed}
                useCombinedContacts
                clearOnSelect
                excludeUserIds={[
                  payloadNew.assigneeUser?.value,
                  ...payloadNew.participants.map((participant) => participant.value),
                ].filter(Boolean)}
              />

              <motion.div
                layout
                className={payloadNew.participants.length > 0 ? 'mt-4' : ''}
                transition={{ layout: { duration: 0.22, ease: 'easeOut' } }}
              >
                <div className="d-flex flex-column gap-2">
                  <AnimatePresence initial={false}>
                    {payloadNew.participants.map((p) => (
                      <motion.div
                        key={p.value}
                        layout
                        initial={{ opacity: 0, y: -8, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.98 }}
                        transition={{ duration: 0.18, ease: 'easeOut' }}
                        className="d-flex align-items-center gap-2 border rounded p-2"
                      >
                        <ProfilePic
                          imgSrc={p.profileImg}
                          altText={p.label}
                          name={p.label}
                          avatarSeed={p.value}
                          width={36}
                          height={35}
                        />

                        <div className="flex-grow-1">
                          <div className="fw-semibold text-sm">{p.label}</div>

                          {p.email && (
                            <div
                              className="text-muted"
                              style={{ fontSize: '12px' }}
                            >
                              {p.email}
                            </div>
                          )}
                        </div>

                        <Button
                          color="link"
                          className="text-danger p-1"
                          onClick={() => {
                            setPayloadNew((prev) => ({
                              ...prev,
                              participants: prev.participants.filter(
                                (item) => item.value !== p.value
                              ),
                            }))
                          }}
                          disabled={isClosed}
                        >
                          <X size={18} />
                        </Button>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </motion.div>
            </FormGroup>

            {isEdit && (
              <FormGroup>
                <Label className="text-sm pb-1">Tags</Label>
                <CreatableSelect
                  isMulti
                  isClearable
                  isDisabled={isClosed}
                  value={payloadNew.tags?.map((t) => ({ label: t, value: t }))}
                  onChange={(newValue) => {
                    setPayloadNew((prev) => ({
                      ...prev,
                      tags: newValue ? newValue.map((v) => v.value) : [],
                    }))
                  }}
                  placeholder="Tambahkan tag..."
                  noOptionsMessage={() => 'Ketik untuk menambah tag baru'}
                  formatCreateLabel={(inputValue) => `Buat "${inputValue}"`}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      borderColor: '#d1d5db',
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: '#d1d5db',
                      },
                      borderRadius: '8px',
                      minHeight: '42px',
                    }),
                  }}
                />
              </FormGroup>
            )}

            <PMBFileUploads
              label="Lampiran"
              multiple={true}
              maxFiles={5}
              accept=".png, .jpeg, .jpg, .pdf"
              value={payloadNew.attachments}
              onChange={(files) => {
                setPayloadNew((prev) => ({
                  ...prev,
                  attachments: files,
                }))
              }}
              disabled={isFieldDisabled}
              uploadParams={{
                userId: 'FILE',
                source: 'PROJECT',
                isPublic: true,
              }}
            />
          </CardBody>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardBody className="d-flex flex-column gap-2">
            <h5 className="mb-2">Target</h5>

            {payloadNew.goals.map((goal, index) => (
              <div
                key={index}
                className={`mb-3 ${index !== payloadNew.goals.length - 1 ? 'pb-3 border-bottom' : ''}`}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <P className="text-sm mb-0 font-weight-bold" color="#101828">
                    Target {index + 1}
                  </P>
                  {payloadNew.goals.length > 1 && (
                    <Button
                      disabled={isFieldDisabled}
                      color="danger"
                      size="sm"
                      outline
                      onClick={() => handleRemoveGoal(index)}
                      className="p-1"
                      style={{ width: '32px', height: '32px' }}
                    >
                      <Trash2 size={16} />
                    </Button>
                  )}
                </div>

                <Row className="align-items-end">
                  <Col md={6}>
                    <FormGroup className="mb-0">
                      <P className="text-sm pb-1" size="regular" color="#344054">
                        Barang
                      </P>
                      <PMBSelect
                        isDisabled={isFieldDisabled}
                        value={
                          formatCommodityOptions(listComodities)?.find(
                            (item) =>
                              item.value?.toString() === goal.itemId?.toString(),
                          ) ||
                          (goal.itemId
                            ? {
                              value: goal.itemId,
                              label: goal.nameItem || '...',
                              unit: goal.unit,
                              unitDisplay: goal.unit
                                ? capitalizeEveryWord(goal.unit)
                                : '',
                            }
                            : null)
                        }
                        options={getAvailableCommodities(index) || []}
                        onChange={(selected) => {
                          handleGoalChange(index, 'itemId', selected?.value || '')
                        }}
                        formatOptionLabel={formatOptionLabel}
                        name={`itemId-${index}`}
                        placeholder="Pilih barang"
                        menuPosition="fixed"
                        menuPlacement="auto"
                        menuPortalTarget={
                          typeof window !== 'undefined' ? document.body : null
                        }
                        styles={{
                          control: (provided) => ({
                            ...provided,
                            borderColor: '#d1d5db',
                            boxShadow: 'none',
                            '&:hover': {
                              borderColor: '#d1d5db',
                            },
                            borderRadius: '8px',
                            padding: '2px',
                            minHeight: '42px',
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999,
                          }),
                        }}
                      />
                    </FormGroup>
                  </Col>

                  <Col md={3}>
                    <FormGroup className="mb-0">
                      <P className="text-sm pb-1" size="regular" color="#344054">
                        Jumlah
                      </P>
                      <PMBInputNumber
                        disabled={isFieldDisabled}
                        onFocus={(e) => e.target.select()}
                        value={formatInputNumber(payloadNew.goals[index].amount)}
                        onChange={(e) => handleAmountChange(e, index, 'amount')}
                        name={`amount-${index}`}
                        placeholder="0"
                        prefix={
                          capitalizeEveryWord(
                            listComodities?.find(
                              (item) =>
                                item.value?.toString() ===
                                goal.itemId?.toString(),
                            )?.unit,
                          ) || ''
                        }
                        isLastPrefix={true}
                      />
                    </FormGroup>
                  </Col>

                  <Col md={3}>
                    <FormGroup className="mb-0">
                      <P className="text-sm pb-1" size="regular" color="#344054">
                        Jumlah Sekarang
                      </P>
                      <PMBInputNumber
                        disabled={!isEdit || isClosed}
                        onFocus={(e) => e.target.select()}
                        value={formatInputNumber(
                          payloadNew.goals[index].currentAmount,
                        )}
                        onChange={(e) =>
                          handleAmountChange(e, index, 'currentAmount')
                        }
                        name={`currentAmount-${index}`}
                        placeholder="0"
                        prefix={
                          capitalizeEveryWord(
                            listComodities?.find(
                              (item) =>
                                item.value?.toString() ===
                                goal.itemId?.toString(),
                            )?.unit,
                          ) || ''
                        }
                        isLastPrefix={true}
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </div>
            ))}

            <div
              className={`mt-2 ${isMobile ? '' : 'd-flex justify-content-end'}`}
            >
              <Button
                color="primary"
                size="sm"
                onClick={handleAddGoal}
                block={isMobile}
                disabled={isFieldDisabled}
              >
                Tambah Target
              </Button>
            </div>
          </CardBody>
        </Card>

        <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-stretch align-items-md-center gap-3">
          <div className="d-flex gap-2">
            <Button
              onClick={() => navigate(-1)}
              outline
              className="btn-cancel"
              block={isMobile}
              style={!isMobile ? { minWidth: '120px' } : {}}
            >
              {isClosed ? 'Kembali' : 'Batal'}
            </Button>
          </div>

          <div className="d-flex gap-2">
            {isEdit && (
              <Button
                disabled={loadingStatus !== null || isClosed}
                color="primary"
                block={isMobile}
                style={!isMobile ? { minWidth: '120px' } : {}}
                onClick={() =>
                  handleSubmit(projectData?.data?.status || payloadNew.status)
                }
              >
                {loadingStatus === 'SAVE' && (
                  <Spinner size="sm" className="me-2" />
                )}
                Simpan
              </Button>
            )}
            {getAvailableStatusButtons().map((btn) => (
              <Button
                key={btn.status}
                disabled={loadingStatus !== null || isClosed}
                color={btn.color}
                outline={btn.status === 'DRAFT'}
                block={isMobile}
                style={!isMobile ? { minWidth: '120px' } : {}}
                onClick={() => handleSubmit(btn.status)}
              >
                {loadingStatus === btn.status && (
                  <Spinner size="sm" className="me-2" />
                )}
                {btn.label}
              </Button>
            ))}
          </div>
        </div>
      </Container>
    </>
  )
}

export default FormAddEditProject
